// Discharge accuracy - what the floor PREDICTED about a discharge, against
// when the patient actually left.
//
// The screen holds no rules: the service turns the server's report into rows
// whose every label is already a string, and this class only decides which
// window to ask for and which row is open. That is what lets it run OnPush
// over a list that can carry a few hundred stays.

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Optional,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  AccuracyFacilityChip,
  AccuracyFigure,
  AccuracyLayout,
  AccuracyReportView,
  AccuracyRowView,
} from './discharge-accuracy.interface';
import {
  compareRowsOf,
  DischargeAccuracyService,
  FacilityCompareKey,
  FacilityCompareRow,
  AccuracyRowGroup,
  blankFigures,
  figuresOf,
  groupRowsByFacility,
  leadFiguresOf,
  mergeTotals,
  sortCompareRows,
  summaryOfTotals,
} from './discharge-accuracy.service';
import { easternToday } from '../../manageb/eastern-date';
import { DISCHARGE_PLAN_URL } from './discharge-accuracy-access';

/** How many facility chips show before the strip folds behind "Show all (n)":
 *  two rows on a desktop. A region with thirty buildings is a list, not a
 *  strip, and a ticked chip is never folded away - a filter the reader cannot
 *  see is one they cannot undo. */
export const CHIPS_BEFORE_FOLD = 12;

export type DischargeWindowMode = 'd30' | 'd90' | 'custom';

/** Every column the reader can order the table by. */
export type AccuracySortKey =
  'patient' | 'facility' | 'admitted' | 'discharged' | 'stay' | 'expected'
  | 'notice' | 'plannedOn' | 'setBy' | 'changes' | 'daysOff';

export interface AccuracySort {
  key: AccuracySortKey;
  /** 1 ascending, -1 descending. */
  dir: 1 | -1;
}

/** One collator for the whole table. Every column tie-breaks on the name, so
 *  this runs on nearly every comparison of every sort; localeCompare with an
 *  options object resolves a collator on each call, and a cached one does the
 *  same comparison without that. */
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const TEXT = (a: string, b: string) => COLLATOR.compare(a || '', b || '');

/** The blanks-last rule for a text column: an empty string compares as less
 *  than every name, which would put the stays nobody planned - exactly the
 *  ones with no "Set by" - at the TOP of an ascending sort. They go last in
 *  both directions, like the day and number columns. */
function byText(a: string, b: string, dir: 1 | -1): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return dir * TEXT(a, b);
}

/**
 * Sorts a column, with the blanks pinned to the BOTTOM in both directions.
 *
 * A row with no plan has no notice and no days-off to speak of. Letting null
 * sort as a number would park every unplanned discharge at one end and read as
 * an ordering of something - "worst notice" - when it is really an ordering of
 * absence. They go last either way, and the reader sorts the rows that have a
 * value against each other.
 */
function byNumber(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir * (a - b);
}

/** The same blanks-last rule for a YYYY-MM-DD day, which orders correctly as
 *  plain text precisely because it is zero-padded and big-endian. */
function byDay(a: string | null, b: string | null, dir: 1 | -1): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return dir * a.localeCompare(b);
}

/** Pure; exported for the tests. The tie-break is always the name, so two
 *  identical reads of the same window agree on the order. */
export function sortRowsBy(rows: ReadonlyArray<AccuracyRowView>, sort: AccuracySort | null): AccuracyRowView[] {
  if (!sort) return [...rows];
  const { key, dir } = sort;
  return [...rows].sort((a, b) => {
    switch (key) {
      case 'patient': return dir * TEXT(a.patientName, b.patientName);
      // One list across every ticked building: this is how a reader gathers
      // one building's rows together without narrowing to it.
      case 'facility': return dir * TEXT(a.facilityName, b.facilityName) || TEXT(a.patientName, b.patientName);
      case 'admitted': return byDay(a.admissionDay, b.admissionDay, dir) || TEXT(a.patientName, b.patientName);
      case 'discharged': return byDay(a.actualDischargeDate, b.actualDischargeDate, dir) || TEXT(a.patientName, b.patientName);
      case 'stay': return byNumber(a.lengthOfStayDays, b.lengthOfStayDays, dir) || TEXT(a.patientName, b.patientName);
      // The ISO instant, which sorts as text for the same reason a ISO day
      // does - it is zero-padded and big-endian.
      case 'plannedOn': return byDay(a.firstPlannedAt, b.firstPlannedAt, dir) || TEXT(a.patientName, b.patientName);
      case 'setBy': return byText(a.setBy, b.setBy, dir) || TEXT(a.patientName, b.patientName);
      // The ISO date, never the rendered label: "Left 9/24" sorts 10/1 above
      // 9/2 as text, which is an ordering of strings pretending to be dates.
      case 'expected': return byDay(a.lastExpectedDate, b.lastExpectedDate, dir) || TEXT(a.patientName, b.patientName);
      case 'notice': return byNumber(a.noticeDays, b.noticeDays, dir) || TEXT(a.patientName, b.patientName);
      case 'changes': return dir * (a.timesDateMoved - b.timesDateMoved) || TEXT(a.patientName, b.patientName);
      case 'daysOff': return byNumber(a.varianceDays, b.varianceDays, dir) || TEXT(a.patientName, b.patientName);
      default: return 0;
    }
  });
}

/** The segmented control, as a closed set with its labels beside it rather
 *  than three strings loose in the template. */
export const WINDOW_MODES: ReadonlyArray<{ id: DischargeWindowMode; label: string }> = [
  { id: 'd30', label: '30 days' },
  { id: 'd90', label: '90 days' },
  { id: 'custom', label: 'Custom' },
];

/** How many days back each preset reaches, INCLUSIVE of today - 30 days means
 *  today and the 29 before it, not today and 30 before it. */
const PRESET_DAYS: Record<'d30' | 'd90', number> = { d30: 30, d90: 90 };

const MS_PER_DAY = 86_400_000;

/** A calendar day N days before the given one, in the same naive YYYY-MM-DD
 *  the whole feature speaks. Built at UTC midnight so a daylight-saving
 *  boundary cannot move it, the same rule the server's readers carry. */
export function daysBefore(day: string, days: number): string {
  const parsed = Date.parse(`${day}T00:00:00.000Z`);
  if (Number.isNaN(parsed)) return day;
  return new Date(parsed - days * MS_PER_DAY).toISOString().slice(0, 10);
}

/** The parts of an HttpErrorResponse this screen reads. `error` is the parsed
 *  body, whatever shape the server gave it. */
export interface LoadFailure {
  status?: number;
  error?: unknown;
}

/** What the server said, when it said something a reader can act on.
 *
 * Two envelopes reach this screen. The controller's own errors - every 400
 * from the window validation, and its 500 - arrive as `{ error: "<text>" }`
 * (dischargePlanController's handleError); the two 403 gates in front of it
 * (role, then client flag) arrive as `{ msg: "<text>" }`. Both are read, and
 * only for 400 and 403: those are the two statuses whose text is written for
 * a reader. A 500's body is a fixed generic sentence either way, and anything
 * else - a proxy's HTML, a network failure's ProgressEvent - is not text at
 * all. Pure; exported for the tests.
 */
export function serverMessageOf(failure: LoadFailure | null | undefined): string {
  if (failure?.status !== 400 && failure?.status !== 403) return '';
  const body = failure.error;
  if (!body || typeof body !== 'object') return '';
  // `error` first, and `msg` only when `error` is absent - never merged. No
  // envelope in this codebase carries both, and an `error` that is present
  // but not text is answered with '' rather than a look at `msg`.
  const text = (body as { error?: unknown }).error ?? (body as { msg?: unknown }).msg;
  return typeof text === 'string' ? text : '';
}

@Component({
  selector: 'ngx-discharge-accuracy',
  templateUrl: './discharge-accuracy.component.html',
  styleUrls: ['./discharge-accuracy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DischargeAccuracyComponent implements OnInit, OnDestroy {
  report: AccuracyReportView | null = null;
  loading = true;
  /** A message the reader can act on, or ''. Never a driver string: the server
   *  keeps those out of the response on purpose. */
  error = '';

  /** The window inputs. Empty means "the server's default", which is the state
   *  the screen opens in - a quarter ending today. */
  from = '';
  to = '';

  /** Which of the three the reader has chosen. The date inputs only exist in
   *  'custom': an empty <input type="date"> renders the browser's own
   *  mm/dd/yyyy chrome, and two of those are not what this screen should open
   *  with when almost everyone wants a round window. */
  mode: DischargeWindowMode = 'd90';
  readonly windowModes = WINDOW_MODES;

  /** One table for every ticked building, or one table per building. The
   *  Discharge Plan screen offers the same pair, so the two read as one. */
  readonly layoutModes: ReadonlyArray<{ id: AccuracyLayout; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'facility', label: 'By facility' },
  ];
  layout: AccuracyLayout = 'all';
  rowGroups: AccuracyRowGroup[] = [];

  setLayout(layout: AccuracyLayout): void {
    if (this.layout === layout) return;
    this.layout = layout;
    this.cdr.markForCheck();
  }

  trackByLayout(_: number, option: { id: AccuracyLayout }): AccuracyLayout {
    return option.id;
  }

  trackByGroup(_: number, group: AccuracyRowGroup): number {
    return group.facilityId;
  }

  /** The building's anticipated share from the strip, for its card head. */
  groupLead(group: AccuracyRowGroup): string {
    const line = this.compareRows.find(row => row.facilityId === group.facilityId);
    return line && line.stays ? line.plannedAheadLabel : '';
  }

  /** The way back to planning forward. Everyone who can open this report has
   *  the Discharge Plan screen, so the link needs no gate of its own. */
  readonly plannerUrl = DISCHARGE_PLAN_URL;

  /** Which rows have their log open, by row key. A Set rather than a flag on
   *  the row so a reload does not close what the reader was reading. */
  private openKeys = new Set<string>();
  private destroy$ = new Subject<void>();

  /** Which buildings the reader has ticked. Every chip ticked is the ONE
   *  representation of "all": an empty set would show the same rows under
   *  chips that all read off. Kept across a reload and pruned to the buildings
   *  the new report knows; not kept across a page load, because a filter
   *  restored days later makes the first paint lie. */
  activeFacilityIds = new Set<number>();
  /** Has the reader touched the chips at all? An untouched empty set is the
   *  first load, which means all; a touched empty set is a deliberate
   *  "clear all", which a reload must not quietly undo. */
  private facilityPickTouched = false;
  chipsExpanded = false;
  /** The chips on screen, cached: the fold and the ticks decide them, and a
   *  getter that sliced on every check would allocate for nothing. */
  visibleChips: AccuracyFacilityChip[] = [];
  /** The bands for the ticked buildings, computed once per change of
   *  selection or report - not in a getter. */
  private selectionLead: AccuracyFigure[] = [];
  private selectionFigures: AccuracyFigure[] = [];

  /** The comparison strip: one line per building, every building, ordered by
   *  the planned-ahead share with the best first - the question the report
   *  was asked, answered per building before the reader digs into rows. */
  compareRows: FacilityCompareRow[] = [];
  compareSort: { key: FacilityCompareKey; dir: 1 | -1 } = { key: 'plannedAhead', dir: -1 };
  /** Folded to its caption bar. A reader who has learned the standings and
   *  wants the rows can put the strip away; it stays open by default because
   *  the standings are the answer to the report's question. */
  compareCollapsed = false;

  /** Where the rows begin. Picking a building on the strip narrows the rows
   *  to it and brings them into view, so the click lands on what it asked
   *  for rather than on a strip that stopped changing. */
  @ViewChild('staysAnchor') private staysAnchor?: ElementRef<HTMLElement>;

  constructor(
    private accuracy: DischargeAccuracyService,
    private cdr: ChangeDetectorRef,
    @Optional() private route?: ActivatedRoute,
  ) { }

  /** Arrived from the Discharge Plan (its link says ?from=plan): the way out
   *  is "back", not "open". The same route either way. */
  get cameFromPlanner(): boolean {
    return this.route?.snapshot?.queryParamMap?.get('from') === 'plan';
  }

  get backLabel(): string {
    return this.cameFromPlanner ? 'Back to Discharge Plan' : 'Open the Discharge Plan';
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.cdr.markForCheck();

    this.accuracy
      .load({ from: this.from || undefined, to: this.to || undefined })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: report => {
          this.report = report;
          // The chosen column and the ticked buildings survive a reload: the
          // reader picked them, and changing the window is not a request to
          // abandon either.
          this.reconcileFacilities();
          this.resort();
          this.refigure();
          this.cdr.markForCheck();
        },
        // The server's 400 messages name the field that was wrong, and its
        // 403s say which gate refused; both are written for a reader. Anything
        // else gets a sentence the reader can act on rather than a status code.
        error: (failure: LoadFailure) => {
          this.report = null;
          this.resort();
          this.refigure();
          this.refreshChips();
          this.error = serverMessageOf(failure)
            || (failure?.status === 403
              ? 'This report is available to administrators only.'
              : 'The report could not be loaded. Try again in a moment.');
          this.cdr.markForCheck();
        },
      });
  }

  /** The reader typed a window; nothing is fetched until they ask for it, so a
   *  half-typed date never round-trips. */
  applyWindow(): void {
    this.load();
  }

  /**
   * Switching the segmented control.
   *
   * 'custom' only REVEALS the inputs - it does not fetch, because the dates
   * standing in them are whatever was there last and asking for that window
   * is not what clicking "Custom" means.
   *
   * '90 days' clears the dates rather than computing them, so the window stays
   * whatever the server calls its default. Computing it here would let the two
   * drift the day somebody changes the default on one side only.
   */
  selectMode(mode: DischargeWindowMode): void {
    if (this.mode === mode && mode !== 'custom') return;
    this.mode = mode;

    if (mode === 'custom') {
      this.cdr.markForCheck();
      return;
    }
    if (mode === 'd90') {
      this.from = '';
      this.to = '';
    } else {
      const today = easternToday();
      this.to = today;
      this.from = daysBefore(today, PRESET_DAYS[mode] - 1);
    }
    this.load();
  }

  isOpen(row: AccuracyRowView): boolean {
    return this.openKeys.has(row.key);
  }

  toggle(row: AccuracyRowView): void {
    if (this.openKeys.has(row.key)) this.openKeys.delete(row.key);
    else this.openKeys.add(row.key);
    this.cdr.markForCheck();
  }

  /** Ordered once, when the report lands or the reader picks a column - not in
   *  a getter. Under OnPush a getter that sorts would re-sort a few hundred
   *  rows on every change-detection pass. */
  get rows(): AccuracyRowView[] {
    return this.sortedRows;
  }

  /** Null = the order the server sent, which is most recent discharge first. */
  sort: AccuracySort | null = null;
  private sortedRows: AccuracyRowView[] = [];

  setSort(key: AccuracySortKey): void {
    this.sort = this.sort && this.sort.key === key
      ? { key, dir: this.sort.dir === 1 ? -1 : 1 }
      : { key, dir: 1 };
    this.resort();
    this.cdr.markForCheck();
  }

  /** 'ascending' / 'descending' for the column the table is ordered by, and
   *  null for the rest - which is what aria-sort expects, not 'none'. */
  ariaSortOf(key: AccuracySortKey): string | null {
    if (this.sort?.key !== key) return null;
    return this.sort.dir === 1 ? 'ascending' : 'descending';
  }

  arrowOf(key: AccuracySortKey): string {
    if (this.sort?.key !== key) return '';
    return this.sort.dir === 1 ? '▲' : '▼';
  }

  /** The rows of the ticked buildings, in the chosen order. With no chips at
   *  all (a server that predates them) every row shows. */
  private resort(): void {
    const rows = this.report?.rows ?? [];
    const scoped = this.facilityChips.length
      ? rows.filter(row => this.activeFacilityIds.has(row.facilityId))
      : rows;
    this.sortedRows = sortRowsBy(scoped, this.sort);
    this.rowGroups = groupRowsByFacility(this.sortedRows);
  }

  get leadFigures(): AccuracyFigure[] {
    return this.selectionLead;
  }

  get figures(): AccuracyFigure[] {
    return this.selectionFigures;
  }

  get hasRows(): boolean {
    return this.rows.length > 0;
  }

  /** Where the record begins, when that moved the window: the report cannot
   *  read a board that did not exist yet, so it starts two days after the
   *  first recorded plan and says so rather than printing a surprise rate over
   *  days nobody could have planned in. '' when the window stood as asked.
   *  The empty state carries the same fact when there are no rows. */
  get recordNote(): string {
    const report = this.report;
    if (!report?.floored || !report.historySince) return '';
    return `Plans have been recorded since ${report.historySince}. The report starts two days after that, `
      + `the first day with a board to read, so this window begins on ${report.from}.`;
  }

  /** No plan has been recorded for this client yet: nothing to read a
   *  discharge against, so nothing to report - which the empty state has to
   *  say, or the reader blames the EHR mirror. */
  get hasNoRecord(): boolean {
    return !!this.report && this.report.floored && !this.report.historySince;
  }

  /** The record exists, but this window ends before the report can start,
   *  or holds no discharge after it starts. */
  get isBeforeRecord(): boolean {
    return !!this.report && this.report.floored && !!this.report.historySince && this.report.rows.length === 0;
  }

  // -- the facility chips ------------------------------------------------------

  /** One per building in the reader's access; empty from an older server. */
  get facilityChips(): AccuracyFacilityChip[] {
    return this.report?.facilities ?? [];
  }

  /** A single building has nothing to split; its name is under every row. */
  get showFacilityChips(): boolean {
    return this.facilityChips.length > 1;
  }

  get facilityCount(): number {
    return this.facilityChips.length;
  }

  get hasFoldedChips(): boolean {
    return this.facilityChips.length > CHIPS_BEFORE_FOLD;
  }

  /** Has the reader actually NARROWED anything? Every chip ticked is the
   *  whole window and must not read as a filter; anything less, down to
   *  nothing ticked at all, is one. */
  get isFacilityFiltered(): boolean {
    const total = this.facilityChips.length;
    return total > 1 && this.activeFacilityIds.size < total;
  }

  /** Nothing ticked: the reader cleared the chips (or unticked the last one)
   *  and has not picked again. The rows and the bands are empty by choice,
   *  and the empty state must say so rather than blame the window. */
  get hasNoFacilitySelected(): boolean {
    return this.facilityChips.length > 0 && this.activeFacilityIds.size === 0;
  }

  get allFacilitiesSelected(): boolean {
    return this.activeFacilityIds.size === this.facilityChips.length;
  }

  /** "3 of 12 facilities" - beside the window in the meta line, so one line
   *  labels both bands and the table. */
  get scopeCaption(): string {
    return `${this.activeFacilityIds.size} of ${this.facilityChips.length} facilities`;
  }

  /** The window has rows, but none at the ticked buildings: an empty
   *  selection, not an empty window, and the empty state says which. */
  get hasHiddenRows(): boolean {
    return !this.hasRows && (this.report?.rows.length ?? 0) > 0;
  }

  get selectedFacilityLabel(): string {
    const n = this.activeFacilityIds.size;
    return n === 1 ? 'this facility' : `these ${n} facilities`;
  }

  toggleFacility(facilityId: number): void {
    const next = new Set(this.activeFacilityIds);
    if (next.has(facilityId)) next.delete(facilityId);
    else next.add(facilityId);
    // Unticking the last chip leaves nothing ticked, and the screen says so:
    // "Select all" is always a click away, so an empty pick can be honest
    // rather than silently turning back into everything.
    this.facilityPickTouched = true;
    this.activeFacilityIds = next;
    this.applySelection();
  }

  selectAllFacilities(): void {
    this.facilityPickTouched = true;
    this.activeFacilityIds = new Set(this.facilityChips.map(chip => chip.facilityId));
    this.applySelection();
  }

  /** Nothing ticked - the start of "just these two" out of fifteen, which
   *  used to take thirteen clicks. */
  clearFacilities(): void {
    this.facilityPickTouched = true;
    this.activeFacilityIds = new Set();
    this.applySelection();
  }

  toggleChipFold(): void {
    this.chipsExpanded = !this.chipsExpanded;
    this.refreshChips();
    this.cdr.markForCheck();
  }

  private applySelection(): void {
    this.resort();
    this.refigure();
    this.refreshChips();
    this.cdr.markForCheck();
  }

  // -- the comparison strip ----------------------------------------------------

  /** Shown with the chips: one building has nothing to compare against. */
  get showCompare(): boolean {
    return this.showFacilityChips;
  }

  setCompareSort(key: FacilityCompareKey): void {
    // A figure column opens with the best first; the name opens A to Z.
    const opening: 1 | -1 = key === 'facility' ? 1 : -1;
    this.compareSort = this.compareSort.key === key
      ? { key, dir: this.compareSort.dir === 1 ? -1 : 1 }
      : { key, dir: opening };
    this.refreshCompare();
    this.cdr.markForCheck();
  }

  compareAriaSort(key: FacilityCompareKey): string | null {
    if (this.compareSort.key !== key) return null;
    return this.compareSort.dir === 1 ? 'ascending' : 'descending';
  }

  compareArrow(key: FacilityCompareKey): string {
    if (this.compareSort.key !== key) return '';
    return this.compareSort.dir === 1 ? '▲' : '▼';
  }

  toggleCompare(): void {
    this.compareCollapsed = !this.compareCollapsed;
    this.cdr.markForCheck();
  }

  /** The strip's line is the only building ticked. */
  isSoloed(facilityId: number): boolean {
    return this.activeFacilityIds.size === 1 && this.activeFacilityIds.has(facilityId);
  }

  /** The strip's one line of help, which says the way BACK once the reader
   *  has narrowed: a pick that only tells you how to narrow further leaves
   *  the whole-portfolio view looking lost. */
  get compareHint(): string {
    if (!this.isFacilityFiltered) return 'Click a facility to see only its stays';
    const picked = this.activeFacilityIds.size;
    if (picked === 1) {
      const only = this.facilityChips.find(chip => this.activeFacilityIds.has(chip.facilityId));
      return `Showing only ${only?.name ?? 'one facility'}. Click it again, or All facilities, to see everyone.`;
    }
    return `Showing ${picked} of ${this.facilityChips.length} facilities. All facilities brings everyone back.`;
  }

  /**
   * A building's line on the strip, clicked: its rows, and only its rows -
   * the cases that explain its number. Clicking the building already on its
   * own brings every building back. The chips follow, since this is the same
   * selection, and the rows are brought into view.
   */
  focusFacility(facilityId: number): void {
    this.facilityPickTouched = true;
    this.activeFacilityIds = this.isSoloed(facilityId)
      ? new Set(this.facilityChips.map(chip => chip.facilityId))
      : new Set([facilityId]);
    this.applySelection();
    this.staysAnchor?.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  private refreshCompare(): void {
    const rows = compareRowsOf(this.facilityChips, this.activeFacilityIds);
    this.compareRows = sortCompareRows(rows, this.compareSort.key, this.compareSort.dir);
  }

  trackByCompareRow(_index: number, row: FacilityCompareRow): number {
    return row.facilityId;
  }

  /** The selection against a fresh report: keep what is still a building the
   *  reader may see. Nothing surviving means all - on the first load, and
   *  when every ticked building left the reader's access - but a selection
   *  the reader cleared on purpose stays cleared. */
  private reconcileFacilities(): void {
    const known = new Set(this.facilityChips.map(chip => chip.facilityId));
    const hadSelection = this.activeFacilityIds.size > 0;
    const kept = new Set([...this.activeFacilityIds].filter(id => known.has(id)));
    const clearedOnPurpose = this.facilityPickTouched && !hadSelection;
    this.activeFacilityIds = kept.size || clearedOnPurpose ? kept : known;
    this.refreshChips();
  }

  private refreshChips(): void {
    // The strip follows the same selection, so it refreshes with the chips.
    this.refreshCompare();
    const chips = this.facilityChips;
    if (this.chipsExpanded || chips.length <= CHIPS_BEFORE_FOLD) {
      this.visibleChips = chips;
      return;
    }
    const shown = chips.slice(0, CHIPS_BEFORE_FOLD);
    // A ticked chip behind the fold would be a filter the reader cannot see.
    const tickedBeyond = this.isFacilityFiltered
      ? chips.slice(CHIPS_BEFORE_FOLD).filter(chip => this.activeFacilityIds.has(chip.facilityId))
      : [];
    this.visibleChips = [...shown, ...tickedBeyond];
  }

  /** The bands for the ticked buildings: their counts added, divided once,
   *  the server's own arithmetic. With no chips the server's figures stand. */
  private refigure(): void {
    const report = this.report;
    if (!report) {
      this.selectionLead = [];
      this.selectionFigures = [];
      return;
    }
    const chips = this.facilityChips;
    if (!chips.length) {
      this.selectionLead = report.leadFigures;
      this.selectionFigures = report.figures;
      return;
    }
    const ticked = chips.filter(chip => this.activeFacilityIds.has(chip.facilityId)).map(chip => chip.totals);
    const summary = summaryOfTotals(mergeTotals(ticked));
    const lead = leadFiguresOf(summary);
    const figures = figuresOf(summary);
    // Nothing ticked is a choice, not a finding: the band goes blank and says
    // so, instead of reading "no discharges" above a panel that says
    // "no facility selected".
    const blank = this.activeFacilityIds.size === 0;
    this.selectionLead = blank ? blankFigures(lead) : lead;
    this.selectionFigures = blank ? blankFigures(figures) : figures;
  }

  trackByChip(_index: number, chip: AccuracyFacilityChip): number {
    return chip.facilityId;
  }

  trackByKey(_index: number, row: AccuracyRowView): string {
    return row.key;
  }

  trackByFigure(_index: number, figure: AccuracyFigure): string {
    return figure.key;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByMode(_index: number, option: { id: DischargeWindowMode }): DischargeWindowMode {
    return option.id;
  }
}
